import { useEffect, useState } from "react";
import { pubSub } from "../ably";
import { MessageText } from "../constants/constants.js";

// Notice for the admin while Ably is permanently disconnected ('failed' or 'suspended',
// see pubSub.isPermanentlyDisconnected). Short 'disconnected' blips never open it; once
// open it stays through the retry states and closes by itself when the connection is
// back. Retry reloads the page for a fresh connection. The admin can also close it (×
// or Escape) to keep working - it then stays closed for this outage and opens again
// only if the connection comes back and is lost again.
export default function RealtimeDownModal() {
  const [down, setDown] = useState(() => pubSub.isPermanentlyDisconnected);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onChange = (next) => {
      setDown(next);
      if (!next) setDismissed(false); // the next outage shows the modal again
    };
    pubSub.onPermanentDisconnectChange(onChange);
    onChange(pubSub.isPermanentlyDisconnected); // a change between render and subscribe
    return () => pubSub.offPermanentDisconnectChange(onChange);
  }, []);

  const open = down && !dismissed;

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setDismissed(true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!open) return null;

  const text = MessageText.realtimeDown;
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 px-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="realtime-down-title"
        aria-describedby="realtime-down-message"
        className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 text-center"
      >
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label={text.close}
          title={text.close}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
          <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v3.75m0 3.75h.008M10.34 3.94l-8.4 14.55A1.5 1.5 0 003.24 21h17.52a1.5 1.5 0 001.3-2.51L13.66 3.94a1.5 1.5 0 00-2.6 0z"
            />
          </svg>
        </div>
        <p id="realtime-down-title" className="text-base font-semibold text-gray-800">
          {text.title}
        </p>
        <p id="realtime-down-message" className="mt-1 text-sm text-gray-500">
          {text.message}
        </p>
        <p className="mt-3 text-sm text-gray-500">
          {text.supportLabel}{" "}
          <a
            href={`mailto:${text.supportEmail}`}
            className="font-medium text-blue-700 underline-offset-2 hover:underline"
          >
            {text.supportEmail}
          </a>
        </p>
        <button
          type="button"
          autoFocus
          onClick={() => window.location.reload()}
          className="mt-5 w-full rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
        >
          {text.retry}
        </button>
      </div>
    </div>
  );
}
